import {
  modTypes,
  type Animation,
  type IVideoPart,
} from "@blings/blings-player";
import { motionAPI } from "@blings/blings-player/editor-tools";
import type {
  LayerTextConnectorActionEvent,
  LayerTransformEvent,
} from "@blings/blings-player/editor-tools";
import _ from "lodash";
import { toJS } from "mobx";
import {
  resolveAssetDetails,
  resolveLayerDetails,
} from "../helpers/layerInspectorPrefill";
import type { LayerInspectorSelection } from "../helpers/layerInspectorTools";
import {
  buildLayerBindingKeys,
  collectLayerBindings,
  normalizeLayerKeyPart,
} from "../helpers/layerSelectionUtils";

type LayerTextChange = {
  path: string;
};

type ModLike = {
  id?: string | number;
  moddata: unknown;
};

type RuntimeVideoPart = {
  data?: unknown;
  modsArr?: Array<Record<string, unknown>>;
};

export type DraftTransformTarget = {
  path: string;
  layerName: string;
  layerUid?: number;
  assetName?: string;
  assetId?: string;
  matchesAsset?: boolean;
};

export function cloneJsonDraft<T>(value: T): T {
  if (value == null) {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

export function cloneVideoPartForPlayer(
  videoPart: IVideoPart & { json?: unknown }
): IVideoPart & { json?: unknown } {
  return cloneJsonDraft(videoPart);
}

function normalizePathInput(path: string): string {
  return path.replace(/\[(\d+)\]/g, ".$1").replace(/^\./, "");
}

export function setExistingPathValue(
  root: unknown,
  path: string,
  value: unknown
): boolean {
  const segments = _.toPath(normalizePathInput(path));
  const key = segments.pop();
  const parent = segments.length ? _.get(root, segments) : root;

  if (!key || !parent || typeof parent !== "object") {
    return false;
  }

  parent[key] = value;
  return true;
}

function canSetPathValue(root: unknown, path: string): boolean {
  const segments = _.toPath(normalizePathInput(path));
  const key = segments.pop();
  const parent = segments.length ? _.get(root, segments) : root;

  return Boolean(key && parent && typeof parent === "object");
}

export function canApplyTextChangesToLayer(
  layer: unknown,
  changes: readonly LayerTextChange[]
): boolean {
  return changes.some((change) => canSetPathValue(layer, change.path));
}

export function getRuntimeControlReportMods(
  player: unknown,
  fallbackMods: unknown[],
  fallbackModIds: Array<string | number | undefined>
): {
  mods: unknown[];
  modIds: Array<string | number | undefined>;
  source: "player.videoParts" | "modsStore";
} {
  const videoParts = (player as { videoParts?: RuntimeVideoPart[] })?.videoParts;
  if (Array.isArray(videoParts)) {
    const runtimeModRecords = videoParts.flatMap((part) => {
      const modsArr = Array.isArray(part?.modsArr) ? part.modsArr : [];
      return modsArr.map((mod) => ({
        mod: {
          ...mod,
          sceneData: part?.data,
        },
        id: mod?.id as string | number | undefined,
      }));
    });

    if (runtimeModRecords.length) {
      return {
        mods: runtimeModRecords.map((record) => record.mod),
        modIds: runtimeModRecords.map((record) => record.id),
        source: "player.videoParts",
      };
    }
  }

  return { mods: fallbackMods, modIds: fallbackModIds, source: "modsStore" };
}

export function getRuntimeAnimationFromPlayer(
  player: unknown
): unknown | undefined {
  const runtimePlayer = player as {
    engine?: { initialJSONVid?: unknown };
    initialJSONVid?: unknown;
    animationData?: unknown;
  };
  return (
    runtimePlayer?.engine?.initialJSONVid ??
    runtimePlayer?.initialJSONVid ??
    runtimePlayer?.animationData
  );
}

function getModTargetCandidates(modData: Record<string, unknown>): unknown[] {
  const candidates: unknown[] = [modData];
  if (Array.isArray(modData.additionals)) {
    candidates.push(...modData.additionals);
  }
  if (
    modData.timeFormatAndLayers &&
    typeof modData.timeFormatAndLayers === "object"
  ) {
    candidates.push(
      ...Object.values(modData.timeFormatAndLayers as Record<string, unknown>)
    );
  }
  candidates.push(modData.showWhenExpired, modData.hideWhenExpired);
  return candidates.filter(Boolean);
}

function buildImageNameBindingKey(imageName: unknown): string | undefined {
  if (typeof imageName !== "string") {
    return undefined;
  }

  const normalized = normalizeLayerKeyPart(imageName);
  return normalized ? `imageName:${normalized}` : undefined;
}

function addLayerBindingKeys(
  keys: Set<string>,
  binding: {
    assetId?: string;
    layerName?: string;
    layerUid?: number;
  }
): void {
  buildLayerBindingKeys(binding).forEach((key) => keys.add(key));
}

function isAnimationLike(animation: unknown): animation is Animation {
  return Boolean(
    animation &&
      typeof animation === "object" &&
      Array.isArray((animation as { layers?: unknown }).layers)
  );
}

function getSelectionConnectionKeys(
  selection: LayerInspectorSelection,
  animation: unknown,
  fallbackAssetId: string
): Set<string> {
  const keys = new Set<string>();

  addLayerBindingKeys(keys, {
    assetId: selection.assetName,
    layerName: selection.layerName,
    layerUid: selection.layerUid,
  });

  if (isAnimationLike(animation)) {
    const resolvedLayer = resolveLayerDetails(
      selection,
      animation,
      fallbackAssetId
    );
    if (resolvedLayer) {
      addLayerBindingKeys(keys, resolvedLayer);
    }

    const assetKey = buildImageNameBindingKey(
      resolveAssetDetails(selection, animation)?.imageName
    );
    if (assetKey) {
      keys.add(assetKey);
    }
  }

  return keys;
}

function getModConnectionKeys(modData: unknown): Set<string> {
  const keys = new Set<string>();
  const modRecord =
    modData && typeof modData === "object"
      ? (modData as Record<string, unknown>)
      : undefined;

  collectLayerBindings(modData, { maxDepth: 10 }).forEach((binding) => {
    addLayerBindingKeys(keys, binding);
  });

  const assetKey = buildImageNameBindingKey(modRecord?.imageName);
  if (assetKey) {
    keys.add(assetKey);
  }

  return keys;
}

function hasMatchingConnectionKey(
  selectionKeys: Set<string>,
  modKeys: Set<string>
): boolean {
  return Array.from(modKeys).some((key) => selectionKeys.has(key));
}

function isDisabledModData(modData: unknown): boolean {
  return Boolean((modData as { isDisabled?: boolean } | undefined)?.isDisabled);
}

export function findStoreModForLayerSelection<T extends ModLike>(
  mods: T[],
  selection: LayerInspectorSelection,
  animation: unknown,
  fallbackAssetId: string
): T | undefined {
  const selectionKeys = getSelectionConnectionKeys(
    selection,
    animation,
    fallbackAssetId
  );
  if (!selectionKeys.size) {
    return undefined;
  }

  const matches = mods
    .map((mod) => ({ mod, modData: toJS(mod.moddata) }))
    .filter(({ modData }) =>
      hasMatchingConnectionKey(selectionKeys, getModConnectionKeys(modData))
    );

  return (
    matches.find(({ modData }) => !isDisabledModData(modData))?.mod ??
    matches[0]?.mod
  );
}

function doesModTargetMatchConnector(
  modTarget: unknown,
  connectorTarget: LayerTextConnectorActionEvent["connector"]["target"]
) {
  if (!modTarget || !connectorTarget || typeof modTarget !== "object") {
    return false;
  }

  const target = modTarget as {
    assetId?: string;
    layerUid?: number;
    layerName?: string;
  };
  const assetMatches =
    !connectorTarget.assetId ||
    !target.assetId ||
    target.assetId === connectorTarget.assetId ||
    target.assetId === connectorTarget.assetName;
  const uidMatches =
    typeof connectorTarget.layerUid === "number" &&
    target.layerUid === connectorTarget.layerUid;
  const nameMatches =
    Boolean(connectorTarget.layerName) &&
    target.layerName === connectorTarget.layerName;

  return uidMatches || (assetMatches && nameMatches);
}

export function findStoreModForTextConnector<T extends ModLike>(
  mods: T[],
  connector: LayerTextConnectorActionEvent["connector"]
): T | undefined {
  const matches = mods.filter((mod) => {
    const modData = toJS(mod.moddata);
    if (!modData || typeof modData !== "object") {
      return false;
    }

    return getModTargetCandidates(modData as Record<string, unknown>).some(
      (target) => doesModTargetMatchConnector(target, connector.target)
    );
  });

  return (
    matches.find(
      (mod) =>
        (toJS(mod.moddata) as { type?: string } | undefined)?.type ===
        modTypes.text
    ) ??
    matches.find(
      (mod) => !(toJS(mod.moddata) as { isDisabled?: boolean })?.isDisabled
    ) ??
    matches[0]
  );
}

export function findDraftLayerTarget(
  animation: unknown,
  target: {
    path?: string;
    layerName?: string;
    layerUid?: number;
    assetName?: string;
    assetId?: string;
  }
): DraftTransformTarget | undefined {
  const animationRecord = animation as {
    layers?: unknown[];
    assets?: unknown[];
  };
  const layerMatches = (layer: unknown) => {
    const layerRecord = layer as { uid?: number; nm?: string } | undefined;
    if (
      typeof target.layerUid === "number" &&
      layerRecord?.uid === target.layerUid
    ) {
      return true;
    }

    return (
      typeof target.layerName === "string" &&
      target.layerName.length > 0 &&
      layerRecord?.nm === target.layerName
    );
  };
  const layerIdentityMatches = (resolvedTarget: DraftTransformTarget) =>
    Boolean(
      resolvedTarget &&
        ((typeof target.layerUid === "number" &&
          resolvedTarget.layerUid === target.layerUid) ||
          resolvedTarget.layerName === target.layerName)
    );
  const assetMatches = (asset: unknown) => {
    const assetRecord = asset as { id?: string; nm?: string } | undefined;
    return Boolean(
      (target.assetId || target.assetName) &&
        (assetRecord?.id === target.assetId ||
          assetRecord?.nm === target.assetId ||
          assetRecord?.id === target.assetName ||
          assetRecord?.nm === target.assetName)
    );
  };
  const rootLayers = Array.isArray(animationRecord?.layers)
    ? animationRecord.layers
    : [];
  const assetEntries = Array.isArray(animationRecord?.assets)
    ? animationRecord.assets
    : [];
  const candidates: DraftTransformTarget[] = [];

  rootLayers.forEach((layer, layerIndex) => {
    const layerRecord = layer as { uid?: number; nm?: string };
    if (!layerMatches(layer)) return;
    candidates.push({
      path: `layers[${layerIndex}]`,
      layerName: layerRecord.nm ?? target.layerName ?? "",
      layerUid: layerRecord.uid,
    });
  });

  assetEntries.forEach((asset, assetIndex) => {
    const assetRecord = asset as {
      id?: string;
      nm?: string;
      layers?: unknown[];
    };
    const layers = Array.isArray(assetRecord?.layers) ? assetRecord.layers : [];
    layers.forEach((layer, layerIndex) => {
      const layerRecord = layer as { uid?: number; nm?: string };
      if (!layerMatches(layer)) return;
      candidates.push({
        path: `assets[${assetIndex}].layers[${layerIndex}]`,
        layerName: layerRecord.nm ?? target.layerName ?? "",
        layerUid: layerRecord.uid,
        assetId: assetRecord?.id,
        assetName: assetRecord?.id ?? assetRecord?.nm ?? target.assetName,
        matchesAsset: assetMatches(asset),
      });
    });
  });

  const preferredTarget = candidates.find((candidate) => candidate.matchesAsset);

  if (preferredTarget) {
    return preferredTarget;
  }

  try {
    const pathTarget = motionAPI.inspect.resolveTarget(animation as any, {
      path: target.path,
    })[0];
    if (layerIdentityMatches(pathTarget)) {
      return {
        path: pathTarget.path,
        layerName: pathTarget.layerName,
        layerUid: pathTarget.layerUid,
        assetId: pathTarget.assetId,
        assetName: pathTarget.assetName ?? target.assetName,
      };
    }
  } catch {
    // Fall back to the best identity match below.
  }

  return candidates[0];
}

export function resolveTransformEventForDraft<
  TEvent extends { target: LayerTransformEvent["target"] },
>(
  animation: unknown,
  event: TEvent
): TEvent {
  const resolvedTarget = findDraftLayerTarget(animation, event.target);

  if (!resolvedTarget) return event;

  return {
    ...event,
    target: {
      ...event.target,
      path: resolvedTarget.path,
      layerName: resolvedTarget.layerName,
      layerUid: resolvedTarget.layerUid,
      assetName: resolvedTarget.assetName ?? event.target.assetName,
    },
  };
}
